import { useHasCustomFunctionality } from '@/hooks/use-has-custom-functionality';
import { useMenu } from '@/hooks/useMenu';
import { useManyPerms, usePerms } from '@/hooks/usePerms';
import { cn } from '@/lib/cn';
import { raise } from '@/lib/utils';
import { useSession } from '@/providers/session-provider';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { ISubMenu } from '../types';
import NotificationIndicator from './notification-indicator';

export function SubMenuItem({
  subMenu,
  notifications,
  isPigelloUser,
  isSystemManager,
  useIcon = false,
}: {
  subMenu: ISubMenu;
  notifications?: Record<string, number | undefined | boolean>;
  isPigelloUser?: boolean;
  isSystemManager?: boolean;
  useIcon?: boolean;
}) {
  const pathname = usePathname();
  const { isEditMode, sidebarSettings } = useMenu();
  const {
    data: {
      user: {
        customerInformation: { is_pigello_main_account },
      },
    },
  } = useSession();
  const modelNames = subMenu.modelNames ?? [];
  const { perms } = useManyPerms(modelNames);
  const { perms: additionalViewPerms } = useManyPerms(
    subMenu?.additionalModelNames ?? []
  );
  const { canView } = usePerms(subMenu.modelName);
  const hasCustomFunctionality = useHasCustomFunctionality(
    subMenu.customFunctionality ?? ''
  );
  const subIsActive =
    subMenu.path &&
    pathname.endsWith(subMenu.path) &&
    ((subMenu.path.includes('contract') && pathname.includes('contract')) ||
      (!subMenu.path.includes('contract') && !pathname.includes('contract')));

  const hasNoPerms = perms.every((p) => !p.canView);
  const hasNotifications =
    notifications && notifications?.[subMenu.key] != null;

  const hasAllAdditionalViewPerms = additionalViewPerms.every((p) => p.canView);

  if (!hasAllAdditionalViewPerms) return null;

  if ((hasNoPerms && modelNames?.length > 0) || (!canView && subMenu.modelName))
    return null;

  if (!subMenu.path) raise('SubMenu path is undefined in SidebarMenuItem');

  if (subMenu.pigelloAdminOnly && is_pigello_main_account === false) {
    return null;
  }

  if (subMenu.pigelloUserOnly && isPigelloUser === false) {
    return null;
  }

  if (
    subMenu.systemManagerOnly &&
    isSystemManager === false &&
    is_pigello_main_account === false
  ) {
    return null;
  }

  if (subMenu.customFunctionality && !hasCustomFunctionality) {
    return null;
  }

  return (
    <Link
      prefetch={'auto'}
      key={subMenu.title}
      className={cn(
        'w-full',
        isEditMode && 'pointer-events-none',
        subMenu.disabled && 'pointer-events-none cursor-default opacity-50'
      )}
      href={subMenu.path}
    >
      <li
        className={cn(
          'my-0.5 list-none truncate',
          isEditMode && 'pl-0',
          sidebarSettings.menuVisibility?.[subMenu.key] && 'opacity-50'
        )}
      >
        <div
          className={cn(
            'flex w-full shrink items-center gap-2 truncate rounded px-2 py-1.5 text-sm text-subtle-foreground transition-colors dark:text-muted-foreground',
            !isEditMode && 'hover:bg-accent',
            subIsActive && 'bg-muted text-foreground'
          )}
        >
          {useIcon && subMenu.icon ? subMenu.icon : <></>}
          <span className='min-w-0 flex-1 truncate'>{subMenu.title}</span>
          {hasNotifications && !isEditMode && (
            <NotificationIndicator
              count={notifications[subMenu.key]}
              className='shrink-0'
              noCheck
              noZero
            />
          )}
        </div>
      </li>
    </Link>
  );
}
